import React, { useCallback, useMemo } from "react";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import type { Level } from "@tiptap/extension-heading";
import styles from "./RichTextEditor.module.css";

import {
    IconBold,
    IconItalic,
    IconStrikethrough,
    IconUnderline,
    IconH1,
    IconH2,
    IconH3,
    IconH4,
    IconH5,
    IconH6,
    IconList,
    IconListNumbers,
    IconUnlink,
    IconLink,
    IconCornerDownRight,
    IconClearFormatting
} from "@tabler/icons-react";

type MenuBarProps = {
    editor: Editor | null;
    expanded?: boolean;
    onToggleLinkPopover?: () => void;
};

const headingIcons: Record<Level, React.ElementType> = {
    1: IconH1,
    2: IconH2,
    3: IconH3,
    4: IconH4,
    5: IconH5,
    6: IconH6
};

const headingLevels: Level[] = [1, 2, 3, 4, 5, 6];

const MenuBar: React.FC<MenuBarProps> = ({ editor, expanded = true, onToggleLinkPopover }) => {
    const preventBlur = useCallback((e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault(), []);

    const makeExec = useCallback(
        (fn: () => void) => () => {
            if (!editor) return;
            editor.chain().focus();
            fn();
        },
        [editor]
    );

    const toggleBold = useMemo(() => makeExec(() => editor?.chain().focus().toggleBold().run()), [editor, makeExec]);
    const toggleItalic = useMemo(
        () => makeExec(() => editor?.chain().focus().toggleItalic().run()),
        [editor, makeExec]
    );
    const toggleStrike = useMemo(
        () => makeExec(() => editor?.chain().focus().toggleStrike().run()),
        [editor, makeExec]
    );
    const toggleUnderline = useMemo(
        () => makeExec(() => editor?.chain().focus().toggleUnderline().run()),
        [editor, makeExec]
    );
    const toggleBulletList = useMemo(
        () => makeExec(() => editor?.chain().focus().toggleBulletList().run()),
        [editor, makeExec]
    );
    const toggleOrderedList = useMemo(
        () => makeExec(() => editor?.chain().focus().toggleOrderedList().run()),
        [editor, makeExec]
    );

    const unsetLink = useCallback(() => {
        editor?.chain().focus().unsetLink().run();
    }, [editor]);

    const handleInsertLineBreak = useCallback(() => {
        editor?.chain().focus().setHardBreak().run();
    }, [editor]);

    const handleClearFormatting = useCallback(() => {
        editor?.chain().focus().unsetAllMarks().run();
    }, [editor]);

    const activeStates = useEditorState({
        editor,
        selector: (ctx) => ({
            bold: ctx.editor?.isActive("bold") ?? false,
            italic: ctx.editor?.isActive("italic") ?? false,
            strike: ctx.editor?.isActive("strike") ?? false,
            underline: ctx.editor?.isActive("underline") ?? false,
            bulletList: ctx.editor?.isActive("bulletList") ?? false,
            orderedList: ctx.editor?.isActive("orderedList") ?? false,
            isLink: ctx.editor?.isActive("link") ?? false,
            heading: headingLevels.reduce((acc, level) => {
                acc[level] = ctx.editor?.isActive("heading", { level }) ?? false;
                return acc;
            }, {} as Record<Level, boolean>)
        })
    });

    const headingHandlers = useMemo(
        () =>
            headingLevels.reduce((acc, level) => {
                acc[level] = makeExec(() => editor?.chain().focus().toggleHeading({ level }).run());
                return acc;
            }, {} as Record<Level, () => void>),
        [editor, makeExec]
    );

    if (!editor) return null;

    return (
        <div
            className={`${styles.toolbar} ${
                expanded ? styles.richTextEditorToolbarAnimateIn : styles.richTextEditorToolbarAnimateOut
            }`}
            aria-hidden={!expanded}
        >
            <div className={styles.controlsGroup}>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={toggleBold}
                    className={`${styles.control} ${activeStates?.bold ? styles.active : ""}`}
                >
                    <IconBold />
                </button>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={toggleItalic}
                    className={`${styles.control} ${activeStates?.italic ? styles.active : ""}`}
                >
                    <IconItalic />
                </button>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={toggleStrike}
                    className={`${styles.control} ${activeStates?.strike ? styles.active : ""}`}
                >
                    <IconStrikethrough />
                </button>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={toggleUnderline}
                    className={`${styles.control} ${activeStates?.underline ? styles.active : ""}`}
                >
                    <IconUnderline />
                </button>
            </div>

            <div className={styles.controlsGroup}>
                {headingLevels.map((level) => {
                    const Icon = headingIcons[level];
                    return (
                        <button
                            type="button"
                            key={level}
                            onMouseDown={preventBlur}
                            onClick={headingHandlers[level]}
                            className={`${styles.control} ${activeStates?.heading[level] ? styles.active : ""}`}
                        >
                            <Icon />
                        </button>
                    );
                })}
            </div>

            <div className={styles.controlsGroup}>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={toggleBulletList}
                    className={`${styles.control} ${activeStates?.bulletList ? styles.active : ""}`}
                >
                    <IconList />
                </button>
                <button
                    type="button"
                    onClick={toggleOrderedList}
                    onMouseDown={preventBlur}
                    className={`${styles.control} ${activeStates?.orderedList ? styles.active : ""}`}
                >
                    <IconListNumbers />
                </button>
            </div>

            <div className={styles.controlsGroup}>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={onToggleLinkPopover}
                    className={`${styles.control} ${activeStates?.isLink ? styles.active : ""}`}
                >
                    <IconLink />
                </button>
                <button
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={unsetLink}
                    disabled={!activeStates?.isLink}
                    className={styles.control}
                >
                    <IconUnlink />
                </button>
            </div>

            <div className={styles.controlsGroup}>
                <button
                    onMouseDown={preventBlur}
                    onClick={handleInsertLineBreak}
                    className={styles.control}
                    type="button"
                >
                    <IconCornerDownRight />
                </button>
                <button
                    onMouseDown={preventBlur}
                    onClick={handleClearFormatting}
                    className={styles.control}
                    type="button"
                >
                    <IconClearFormatting />
                </button>
            </div>
        </div>
    );
};

export default MenuBar;
